import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "@coreui/coreui-pro"

const PAGE_SIZES = [10, 25, 50, 100]

export default class extends Controller {
  static targets = ["content"]

  static values = {
    url: String,
    htmlUrl: String,
    csvUrl: String,
    errorMessage: String
  }

  connect() {
    this.currentPage = 1
    this.pageSize = 25
    this.abortController = new AbortController()
    this.observeLayoutHeight()
    this.load()
  }

  disconnect() {
    this.abortController.abort()
    this.layoutObserver.disconnect()
    this.departmentDropdown?.dispose()
    this.destroySelectize()
  }

  observeLayoutHeight() {
    const layout = this.element.closest(".wrapper")
    const chrome = Array.from(layout?.querySelectorAll(":scope > .header, :scope > .footer") || [])
    const updateHeight = () => {
      const height = chrome.reduce((total, element) => total + element.getBoundingClientRect().height, 0)
      this.element.style.setProperty("--working-hours-layout-height", `${height}px`)
    }

    this.layoutObserver = new ResizeObserver(updateHeight)
    chrome.forEach((element) => this.layoutObserver.observe(element))
    updateHeight()
  }

  async load() {
    try {
      const response = await fetch(this.urlWithCurrentQuery(this.urlValue), {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: this.abortController.signal
      })
      const payload = await response.json()

      if (!response.ok) throw new Error(payload.error || this.errorMessageValue)

      this.payload = payload
      this.renderReport()
    } catch (error) {
      if (error.name !== "AbortError") this.renderError(error.message || this.errorMessageValue)
    }
  }

  renderReport() {
    this.departmentDropdown?.dispose()
    this.destroySelectize()
    this.contentTarget.replaceChildren(
      this.buildHeading(),
      this.buildFilters(),
      this.buildReportCard()
    )
    this.initializeSelectize()
  }

  renderError(message) {
    const card = this.elementFor("div", "card shadow-sm")
    const body = this.elementFor("div", "card-body")
    const alert = this.elementFor("div", "alert alert-danger mb-0", message)
    alert.setAttribute("role", "alert")
    body.append(alert)
    card.append(body)
    this.contentTarget.replaceChildren(card)
  }

  buildHeading() {
    const wrapper = this.elementFor("div", "d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3")
    const copy = document.createElement("div")
    const title = this.elementFor("h1", "h3 mb-1", this.payload.meta.title)
    const subtitle = this.elementFor("p", "text-body-secondary mb-0", this.payload.meta.subtitle)
    copy.append(title, subtitle)
    wrapper.append(copy)

    if (this.payload.meta.refresh_time) {
      const badge = this.elementFor("span", "badge bg-info-subtle text-info-emphasis")
      const icon = this.elementFor("i", "fa-regular fa-arrows-rotate me-1")
      icon.setAttribute("aria-hidden", "true")
      badge.append(icon, document.createTextNode(this.payload.meta.refresh_time))
      wrapper.append(badge)
    }

    return wrapper
  }

  buildFilters() {
    const card = this.elementFor("div", "card shadow-sm mb-3")
    const body = this.elementFor("div", "card-body")
    const form = this.elementFor("form", "row g-3 align-items-end")
    form.action = this.htmlUrlValue
    form.method = "get"

    const dimension = document.createElement("input")
    dimension.type = "hidden"
    dimension.name = "dimension"
    dimension.value = this.payload.meta.dimension
    form.append(dimension)

    const company = this.buildSelect({
      id: "company_code",
      name: "company_code",
      options: this.payload.filters.companies,
      selectedValues: [this.payload.filters.company_code],
      disabled: this.payload.filters.companies.length === 0
    })
    const departments = this.buildDepartmentPicker(form)
    const startDate = this.buildDateInput("start_date", this.payload.filters.start_date)
    const endDate = this.buildDateInput("end_date", this.payload.filters.end_date)
    const auditMode = this.buildSelect({
      id: "audit_mode",
      name: "audit_mode",
      options: this.payload.filters.audit_modes,
      selectedValues: [this.payload.meta.audit_mode]
    })

    form.append(
      this.buildFilterField("col-sm-6 col-xl-2", this.payload.labels.filters.company, company),
      this.buildFilterField("col-sm-6 col-xl-3", this.payload.labels.filters.departments, departments, "department_codes"),
      this.buildFilterField("col-sm-6 col-xl-2", this.payload.labels.filters.start_date, startDate),
      this.buildFilterField("col-sm-6 col-xl-2", this.payload.labels.filters.end_date, endDate),
      this.buildFilterField("col-sm-6 col-xl-2", this.payload.labels.filters.audit_mode, auditMode)
    )

    const buttonWrapper = this.elementFor("div", "col-sm-6 col-xl-1 d-grid")
    const button = this.elementFor("button", "btn btn-primary")
    button.type = "submit"
    const icon = this.elementFor("i", "fa-regular fa-magnifying-glass me-1")
    icon.setAttribute("aria-hidden", "true")
    button.append(icon, document.createTextNode(this.payload.labels.actions.query))
    buttonWrapper.append(button)
    form.append(buttonWrapper)

    body.append(form)
    card.append(body)
    this.selectElements = [company, auditMode]
    return card
  }

  buildDepartmentPicker(form) {
    const labels = this.payload.labels.department_picker
    const options = this.payload.filters.departments
    const selected = new Set(this.payload.filters.department_codes)
    const wrapper = this.elementFor("div", "dropdown working-hours-department-picker")
    const button = this.elementFor("button", "form-select text-start working-hours-department-toggle")
    button.type = "button"
    button.id = "department_codes"
    button.disabled = options.length === 0
    button.dataset.coreuiToggle = "dropdown"
    button.setAttribute("aria-expanded", "false")
    button.setAttribute("aria-controls", "working-hours-department-menu")
    const summary = this.elementFor("span", "d-block text-truncate")
    summary.setAttribute("aria-live", "polite")
    button.append(summary)

    const menu = this.elementFor("div", "dropdown-menu working-hours-department-menu p-2 shadow")
    menu.id = "working-hours-department-menu"
    const search = this.elementFor("input", "form-control mb-2")
    search.type = "search"
    search.placeholder = labels.search
    search.setAttribute("aria-label", labels.search)
    const actions = this.elementFor("div", "d-flex justify-content-between mb-2")
    const selectVisible = this.elementFor("button", "btn btn-sm btn-outline-primary", labels.select_visible)
    const clear = this.elementFor("button", "btn btn-sm btn-outline-secondary", labels.clear)
    selectVisible.type = clear.type = "button"
    actions.append(selectVisible, clear)
    const list = this.elementFor("div", "working-hours-department-options")
    list.setAttribute("role", "group")
    list.setAttribute("aria-label", this.payload.labels.filters.departments)
    const choices = options.map(({value, label}) => {
      const row = this.elementFor("label", "working-hours-department-option")
      const checkbox = this.elementFor("input", "form-check-input m-0 flex-shrink-0")
      checkbox.type = "checkbox"
      checkbox.name = "department_codes[]"
      checkbox.value = value
      checkbox.checked = selected.has(value)
      row.append(checkbox, this.elementFor("span", "", label))
      list.append(row)
      return {row, checkbox, label}
    })
    const empty = this.elementFor("div", "text-body-secondary p-2", labels.no_results)
    empty.hidden = true
    list.append(empty)
    const error = this.elementFor("div", "text-danger small mt-2", labels.required)
    error.setAttribute("role", "alert")
    error.hidden = true
    menu.append(search, actions, list, error)
    wrapper.append(button, menu)
    this.departmentDropdown = new Dropdown(button, {autoClose: "outside"})

    const updateSummary = () => {
      const count = choices.filter(({checkbox}) => checkbox.checked).length
      summary.textContent = count ? this.interpolate(labels.selected, {count}) : labels.placeholder
      if (count) error.hidden = true
    }
    const filterChoices = () => {
      const query = search.value.trim().toLocaleLowerCase()
      choices.forEach(({row, checkbox, label}) => {
        row.hidden = !`${label} ${checkbox.value}`.toLocaleLowerCase().includes(query)
      })
      const noResults = choices.every(({row}) => row.hidden)
      empty.hidden = !noResults
      selectVisible.disabled = noResults
    }
    list.addEventListener("change", updateSummary)
    search.addEventListener("input", filterChoices)
    search.addEventListener("keydown", (event) => {
      if (event.key === "Enter") event.preventDefault()
    })
    selectVisible.addEventListener("click", () => {
      choices.forEach(({row, checkbox}) => { if (!row.hidden) checkbox.checked = true })
      updateSummary()
    })
    clear.addEventListener("click", () => {
      choices.forEach(({checkbox}) => { checkbox.checked = false })
      updateSummary()
    })
    button.addEventListener("shown.coreui.dropdown", () => search.focus())
    button.addEventListener("hidden.coreui.dropdown", () => {
      search.value = ""
      filterChoices()
    })
    form.addEventListener("submit", (event) => {
      if (choices.length && !choices.some(({checkbox}) => checkbox.checked)) {
        event.preventDefault()
        error.hidden = false
        this.departmentDropdown.show()
        search.focus()
      }
    })
    updateSummary()
    return wrapper
  }

  buildReportCard() {
    const card = this.elementFor("div", "card shadow-sm working-hours-report-card")
    card.append(this.buildReportCardHeader())

    if (this.payload.filters.companies.length === 0 || this.payload.filters.departments.length === 0) {
      card.append(this.buildMessage(this.payload.labels.no_authorized_data, "alert alert-warning mb-0"))
    } else if (this.payload.rows.length === 0) {
      card.append(this.buildEmptyMessage())
    } else {
      card.append(this.buildTable(), this.buildPagination())
      this.renderRows()
    }

    return card
  }

  buildReportCardHeader() {
    const header = this.elementFor("div", "card-header d-flex flex-wrap align-items-center justify-content-between gap-2")
    const nav = this.elementFor("ul", "nav nav-pills card-header-pills")
    nav.setAttribute("aria-label", this.payload.labels.dimensions_label)

    Object.entries(this.payload.labels.dimensions).forEach(([dimension, label]) => {
      const item = this.elementFor("li", "nav-item")
      const active = this.payload.meta.dimension === dimension
      const link = this.elementFor("a", `nav-link${active ? " active" : ""}`, label)
      link.href = this.urlWithCurrentQuery(this.htmlUrlValue, {dimension})
      if (active) link.setAttribute("aria-current", "page")
      item.append(link)
      nav.append(item)
    })

    const exportLink = this.elementFor("a", "btn btn-outline-primary btn-sm")
    exportLink.href = this.urlWithCurrentQuery(this.csvUrlValue)
    const icon = this.elementFor("i", "fa-regular fa-file-arrow-down me-1")
    icon.setAttribute("aria-hidden", "true")
    exportLink.append(icon, document.createTextNode(this.payload.labels.actions.export))
    header.append(nav, exportLink)
    return header
  }

  buildTable() {
    this.tableWrapper = this.elementFor("div", "working-hours-table-wrapper")
    const table = this.elementFor("table", "table table-bordered table-hover align-middle mb-0 working-hours-table")
    const head = document.createElement("thead")
    const headingRow = document.createElement("tr")

    headingRow.append(
      this.buildHeadingCell(this.payload.labels.table.sequence, "working-hours-sticky working-hours-sequence text-center"),
      this.buildHeadingCell(this.payload.labels.table.code, "working-hours-sticky working-hours-code text-center"),
      this.buildHeadingCell(this.payload.labels.table.name, "working-hours-sticky working-hours-name text-center"),
      this.buildHeadingCell(this.payload.labels.table.audit_status, "working-hours-sticky working-hours-audit text-center")
    )

    this.payload.days.forEach((day) => {
      const className = `working-hours-day text-center${day.workday ? "" : " working-hours-nonwork"}`
      const cell = this.elementFor("th", className)
      const date = this.elementFor("span", "d-block text-nowrap", day.date)
      const weekday = this.elementFor("small", "fw-normal", day.weekday)
      cell.append(date, weekday)
      headingRow.append(cell)
    })
    headingRow.append(this.buildHeadingCell(this.payload.labels.table.total, "working-hours-total text-center"))

    head.append(headingRow)
    this.tableBody = document.createElement("tbody")
    table.append(head, this.tableBody)
    this.tableWrapper.append(table)
    return this.tableWrapper
  }

  buildPagination() {
    const footer = this.elementFor("div", "card-footer d-flex flex-wrap align-items-center justify-content-between gap-2")
    const pageSizeGroup = this.elementFor("div", "d-flex align-items-center gap-2")
    const label = this.elementFor("label", "mb-0", this.payload.labels.pagination.rows_per_page)
    const select = this.elementFor("select", "form-select form-select-sm working-hours-page-size")
    PAGE_SIZES.forEach((size) => {
      const option = this.elementFor("option", "", size)
      option.value = size
      option.selected = size === this.pageSize
      select.append(option)
    })
    select.addEventListener("change", (event) => {
      this.pageSize = Number(event.target.value)
      this.currentPage = 1
      this.renderRows()
    })
    label.htmlFor = "working-hours-page-size"
    select.id = "working-hours-page-size"
    pageSizeGroup.append(label, select)

    this.paginationSummary = this.elementFor("div", "text-body-secondary")
    const buttons = this.elementFor("div", "btn-group btn-group-sm")
    this.previousButton = this.elementFor("button", "btn btn-outline-primary", this.payload.labels.pagination.previous)
    this.nextButton = this.elementFor("button", "btn btn-outline-primary", this.payload.labels.pagination.next)
    this.previousButton.type = "button"
    this.nextButton.type = "button"
    this.previousButton.addEventListener("click", () => this.changePage(-1))
    this.nextButton.addEventListener("click", () => this.changePage(1))
    buttons.append(this.previousButton, this.nextButton)
    footer.append(pageSizeGroup, this.paginationSummary, buttons)
    return footer
  }

  renderRows() {
    const startIndex = (this.currentPage - 1) * this.pageSize
    const visibleRows = this.payload.rows.slice(startIndex, startIndex + this.pageSize)
    const fragment = document.createDocumentFragment()

    visibleRows.forEach((row, localIndex) => {
      this.payload.meta.row_kinds.forEach((kind, kindIndex) => {
        const tableRow = document.createElement("tr")

        if (kindIndex === 0) {
          tableRow.classList.add("working-hours-group-start")
          tableRow.append(
            this.buildRowspanCell(startIndex + localIndex + 1, "working-hours-sticky working-hours-sequence text-center"),
            this.buildRowspanCell(row.code, "working-hours-sticky working-hours-code text-center text-nowrap"),
            this.buildRowspanCell(row.name, "working-hours-sticky working-hours-name")
          )
        }

        const audit = this.elementFor("th", "working-hours-sticky working-hours-audit text-nowrap", this.payload.labels.table.row_kinds[kind])
        audit.scope = "row"
        tableRow.append(audit)

        this.payload.days.forEach((day) => {
          tableRow.append(this.buildDataCell(row.entries[kind][day.date], day.workday))
        })

        const total = this.elementFor("td", "working-hours-total text-center fw-semibold text-nowrap", `${this.formatHours(row.totals[kind])}h`)
        tableRow.append(total)
        fragment.append(tableRow)
      })
    })

    this.tableBody.replaceChildren(fragment)
    this.updatePagination(startIndex, visibleRows.length)
    this.tableWrapper.scrollTop = 0
  }

  buildDataCell(value, workday) {
    const className = `working-hours-day${workday ? "" : " working-hours-nonwork"}`
    const cell = this.elementFor("td", className)

    if (value === undefined || value === null) {
      cell.append(this.elementFor("span", "text-body-secondary", "—"))
    } else if (this.payload.meta.daily_totals) {
      cell.append(this.elementFor("span", "fw-semibold", `${this.formatHours(value)}h`))
    } else {
      value.forEach(([label, hours]) => {
        const entry = this.elementFor("div", "working-hours-entry")
        entry.append(
          this.elementFor("span", "working-hours-entry-label", label),
          this.elementFor("span", "text-body-secondary", `${this.formatHours(hours)}h`)
        )
        entry.firstChild.append(document.createTextNode(":"))
        cell.append(entry)
      })
    }

    return cell
  }

  updatePagination(startIndex, visibleCount) {
    const total = this.payload.rows.length
    const totalPages = Math.ceil(total / this.pageSize)
    this.currentPage = Math.min(this.currentPage, totalPages)
    this.paginationSummary.textContent = this.interpolate(this.payload.labels.pagination.summary, {
      start: startIndex + 1,
      end: startIndex + visibleCount,
      total
    })
    this.previousButton.disabled = this.currentPage <= 1
    this.nextButton.disabled = this.currentPage >= totalPages
  }

  changePage(offset) {
    const totalPages = Math.ceil(this.payload.rows.length / this.pageSize)
    const nextPage = Math.max(1, Math.min(this.currentPage + offset, totalPages))
    if (nextPage === this.currentPage) return

    this.currentPage = nextPage
    this.renderRows()
  }

  buildSelect({id, name, options, selectedValues, multiple = false, disabled = false}) {
    const select = this.elementFor("select", "form-control")
    const selected = new Set(selectedValues.filter((value) => value !== null))
    select.id = id
    select.name = name
    select.multiple = multiple
    select.disabled = disabled
    options.forEach(({value, label}) => {
      const option = this.elementFor("option", "", label)
      option.value = value
      option.selected = selected.has(value)
      select.append(option)
    })
    return select
  }

  buildDateInput(id, value) {
    const input = this.elementFor("input", "form-control")
    input.type = "date"
    input.id = id
    input.name = id
    input.value = value
    input.required = true
    return input
  }

  buildFilterField(className, labelText, control, controlId = control.id) {
    const wrapper = this.elementFor("div", className)
    const label = this.elementFor("label", "form-label", labelText)
    label.htmlFor = controlId
    wrapper.append(label, control)
    return wrapper
  }

  buildHeadingCell(text, className) {
    return this.elementFor("th", className, text)
  }

  buildRowspanCell(text, className) {
    const cell = this.elementFor("td", className, text)
    cell.rowSpan = this.payload.meta.row_kinds.length
    return cell
  }

  buildMessage(message, alertClass) {
    const body = this.elementFor("div", "card-body")
    const alert = this.elementFor("div", alertClass, message)
    alert.setAttribute("role", "alert")
    body.append(alert)
    return body
  }

  buildEmptyMessage() {
    const body = this.elementFor("div", "card-body")
    const empty = this.elementFor("div", "text-center text-body-secondary py-5")
    const icon = this.elementFor("i", "fa-regular fa-calendar-xmark fa-2x mb-3")
    icon.setAttribute("aria-hidden", "true")
    empty.append(icon, this.elementFor("p", "mb-0", this.payload.labels.no_data))
    body.append(empty)
    return body
  }

  initializeSelectize() {
    if (!$.fn.selectize) return

    this.selectizeControls = this.selectElements.map((select) => {
      const options = select.multiple ? {plugins: ["remove_button"], respect_word_boundaries: false} : {}
      return $(select).selectize(options)[0].selectize
    })
  }

  destroySelectize() {
    if (!this.selectizeControls) return

    this.selectizeControls.forEach((control) => control.destroy())
    this.selectizeControls = null
  }

  urlWithCurrentQuery(baseUrl, overrides = {}) {
    const url = new URL(baseUrl, window.location.origin)
    url.search = window.location.search
    Object.entries(overrides).forEach(([key, value]) => url.searchParams.set(key, value))
    return `${url.pathname}${url.search}`
  }

  formatHours(value) {
    return Number(value).toFixed(1).replace(/\.0$/, "")
  }

  interpolate(template, values) {
    return template.replace(/%\{(\w+)\}/g, (_match, key) => values[key])
  }

  elementFor(tagName, className = "", text = null) {
    const element = document.createElement(tagName)
    if (className) element.className = className
    if (text !== null && text !== undefined) element.textContent = text
    return element
  }
}
